import React, { useState, useEffect, useCallback } from "react";
import {
  X,
  Calendar,
  Tag,
  FileText,
  Clock,
  Save,
  Coins,
  Users,
  Search,
  Plus,
  Trash2,
  Upload,
  File,
  Download,
  CheckSquare,
  Square,
} from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { SimpleDropdown } from "./ui/simple-dropdown";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import MultiSelectDropdown from "./ui/multi-select-dropdown";
import StudentCreateForm from "./StudentCreateForm";
import { NumericFormat } from "react-number-format";
import { educationAPI, financeAPI, agreementsAPI } from "../api/axiosClient";
import {
  UpdateEventRequest,
  Event,
  EventType,
  Currency,
  Student,
  Receivable,
} from "../api/types";
import { toast } from "react-hot-toast";

interface EventEditFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  editingEventId: string | null;
  schoolId: string;
}

const EventEditForm: React.FC<EventEditFormProps> = ({
  isOpen,
  onClose,
  onSuccess,
  editingEventId,
  schoolId,
}) => {
  const [formData, setFormData] = useState<UpdateEventRequest>({
    id: "",
    name: "",
    description: "",
    startsAt: "",
    endsAt: "",
    lastPaymentDate: "",
    eventType: EventType.Trip,
    price: 0,
    currency: Currency.TRY,
    quotaType: 0,
    quota: 0,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPaidEvent, setIsPaidEvent] = useState<boolean>(false);
  const [hasLimitedQuota, setHasLimitedQuota] = useState<boolean>(false);
  const [quota, setQuota] = useState<number | undefined>(undefined);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});

  // Tab state
  const [activeTab, setActiveTab] = useState<
    "event" | "students" | "documents" | "payments"
  >("event");

  // Student assignment state
  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Student[]>([]);
  const [assignedStudents, setAssignedStudents] = useState<Student[]>([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedSearchStudents, setSelectedSearchStudents] = useState<
    string[]
  >([]);
  const [selectedAssignedStudents, setSelectedAssignedStudents] = useState<
    string[]
  >([]);

  // Classroom filter states
  const [classrooms, setClassrooms] = useState<any[]>([]);
  const [loadingClassrooms, setLoadingClassrooms] = useState(false);
  const [selectedClassrooms, setSelectedClassrooms] = useState<string[]>([]);

  // Document management state
  const [documents, setDocuments] = useState<any[]>([]);
  const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
  const [selectedDocumentsToDelete, setSelectedDocumentsToDelete] = useState<
    string[]
  >([]);

  // Student create form state
  const [showStudentCreateForm, setShowStudentCreateForm] = useState(false);
  const [uploadStatus, setUploadStatus] = useState<{
    [key: string]: "pending" | "uploading" | "success" | "error";
  }>({});
  const [deleteStatus, setDeleteStatus] = useState<{
    [key: string]: "pending" | "deleting" | "success" | "error";
  }>({});
  const [isUploadingAny, setIsUploadingAny] = useState(false);

  // Payment data state
  const [receivables, setReceivables] = useState<Receivable[]>([]);
  const [showSyncConfirmation, setShowSyncConfirmation] = useState(false);
  const [isSyncing, setIsSyncing] = useState(false);
  const [isActivated, setIsActivated] = useState<boolean>(false);

  const eventTypeOptions = [
    { id: EventType.Trip, value: EventType.Trip, label: "Gezi" },
    { id: EventType.Vacation, value: EventType.Vacation, label: "Tatil" },
    { id: EventType.Concert, value: EventType.Concert, label: "Konser" },
    { id: EventType.Theater, value: EventType.Theater, label: "Tiyatro" },
    {
      id: EventType.MuseumVisit,
      value: EventType.MuseumVisit,
      label: "Müze Ziyareti",
    },
    { id: EventType.Workshop, value: EventType.Workshop, label: "Atölye" },
    { id: EventType.Seminar, value: EventType.Seminar, label: "Seminer" },
    { id: EventType.Sports, value: EventType.Sports, label: "Spor Etkinliği" },
    {
      id: EventType.Competition,
      value: EventType.Competition,
      label: "Yarışma",
    },
    {
      id: EventType.Festival,
      value: EventType.Festival,
      label: "Festival/Fuar",
    },
    {
      id: EventType.Graduation,
      value: EventType.Graduation,
      label: "Mezuniyet",
    },
    {
      id: EventType.Orientation,
      value: EventType.Orientation,
      label: "Oryantasyon",
    },
    { id: EventType.Picnic, value: EventType.Picnic, label: "Piknik" },
    { id: EventType.Charity, value: EventType.Charity, label: "Hayır/Bağış" },
    { id: EventType.Exam, value: EventType.Exam, label: "Sınav" },
    {
      id: EventType.SchoolPayment,
      value: EventType.SchoolPayment,
      label: "Okul Ödemesi",
    },
    { id: EventType.Other, value: EventType.Other, label: "Diğer" },
  ];

  const currencyOptions = [
    { id: Currency.TRY, value: Currency.TRY, label: "₺ TRY" },
    // String değerleri de kabul et
    { id: "TRY", value: "TRY", label: "₺ TRY" },
  ];

  // Helper function to convert UTC ISO string to local datetime string for input
  const convertUTCToLocal = (utcDateTime: string): string => {
    if (!utcDateTime) return "";
    const date = new Date(utcDateTime);
    // Convert to local timezone and format for datetime-local input
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    const hours = String(date.getHours()).padStart(2, "0");
    const minutes = String(date.getMinutes()).padStart(2, "0");
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  };

  const loadClassrooms = useCallback(async () => {
    setLoadingClassrooms(true);
    try {
      const response = await educationAPI.getClassrooms({
        Q: "",
        Page: 1,
        Size: 100,
        OrderBy: 0,
        SortBy: "",
      });

      if (response.success && response.data?.items) {
        setClassrooms(response.data.items);
      }
    } catch (error) {
      console.error("Error loading classrooms:", error);
      toast.error("Sınıflar yüklenirken hata oluştu");
    } finally {
      setLoadingClassrooms(false);
    }
  }, []);

  const loadPaymentData = useCallback(async () => {
    if (!editingEventId) return;

    try {
      const response = await financeAPI.getEventReceivables(editingEventId, {
        Page: 1,
        Size: 100,
        OrderBy: 0,
      });

      if (response.success && response.data?.items) {
        setReceivables(response.data.items);
      }
    } catch (error) {
      console.error("Error loading payment data:", error);
    }
  }, [editingEventId]);

  const loadEventDetails = useCallback(async () => {
    if (!editingEventId) return;

    setIsLoading(true);
    try {
      const response = await educationAPI.getEventDetails(editingEventId);
      if (response.success && response.data) {
        const event = response.data as Event;
        await loadEventDocuments(event.eventId);
        // API returns quotaTypeId as numeric value (1 = limited, 0 = unlimited)
        const quotaTypeValue =
          (event as any).quotaTypeId ?? event.quotaType ?? 0;
        setFormData({
          id: event.eventId,
          name: event.name,
          description: event.description,
          startsAt: convertUTCToLocal(event.startsAt),
          endsAt: convertUTCToLocal(event.endsAt),
          lastPaymentDate: convertUTCToLocal(event.lastPaymentDate),
          eventType: event.eventType,
          price: event.price,
          currency: event.currency,
          quotaType: quotaTypeValue,
          quota: event.quota ?? 0,
        });

        // Set paid event status based on price
        setIsPaidEvent(event.price > 0);

        // Set quota status based on quotaTypeId (1 = limited, 0 = unlimited)
        setHasLimitedQuota(quotaTypeValue === 1);
        setQuota(event.quota ?? undefined);

        // Set activated status
        setIsActivated((event as any).activated || false);

        // Load assigned students from registrations
        if (event.registrations && event.registrations.length > 0) {
          const assignedStudentsData = event.registrations.map(
            (registration) => ({
              id: registration.studentId, // Fallback for compatibility
              studentId: registration.studentId,
              firstName: registration.studentName.split(" ")[0] || "",
              lastName:
                registration.studentName.split(" ").slice(1).join(" ") || "",
              studentNumber: registration.studentNumber,
              className: registration.classroomName,
              classroomName: registration.classroomName,
              parents: registration.parents || [],
              birthDate: "",
              gender: 0,
              phoneCode: "",
              phoneNumber: "",
              enrollmentDate: "",
              identityNumber: "",
              status: 0,
              schoolId: event.schoolId,
              schoolName: event.schoolName,
              classroomId: "",
              parentIds: registration.parents
                ? registration.parents.map((p) => p.parentId)
                : [],
              isActive: true,
              createdAt: registration.registeredAt,
              updatedAt: registration.registeredAt,
              paymentStatus: registration.paymentStatus || "Unpaid", // Add paymentStatus field
            }),
          );
          setAssignedStudents(assignedStudentsData);
        }

        // Load documents from agreements
        if (event.agreements && event.agreements.length > 0) {
          const documentsData = event.agreements.map((agreement) => ({
            id: agreement.agreementDocumentId, // Use agreementDocumentId for deletion
            eventAgreementDocumentId: agreement.eventAgreementDocumentId,
            agreementDocumentVersionId: agreement.agreementDocumentVersionId,
            agreementDocumentId: agreement.agreementDocumentId,
            name: agreement.name,
            size: agreement.size,
            type: agreement.name.split(".").pop()?.toLowerCase() || "unknown",
            uploadedAt: agreement.createdAt,
            version: agreement.version,
            isFromDatabase: true, // Flag to distinguish from uploaded files
          }));
          setDocuments(documentsData);
        }

        // Load payment data
        loadPaymentData();
      }
    } catch (error: any) {
      console.error("Error loading event details:", error);
      toast.error(
        error.response?.data?.message ||
          "Etkinlik bilgileri yüklenirken bir hata oluştu",
      );
    } finally {
      setIsLoading(false);
    }
  }, [editingEventId, loadPaymentData]);

  // Load all students when form opens
  const loadAllStudents = useCallback(async () => {
    setIsSearching(true);
    try {
      const response = await educationAPI.getStudents({
        Q: "",
        Page: 1,
        Size: 1000, // Tüm öğrencileri yükle
        OrderBy: 0,
        SortBy: "",
      });

      if (response.data && response.data.items) {
        setSearchResults(response.data.items);
      }
    } catch (error) {
      console.error("Error loading all students:", error);
      toast.error("Öğrenciler yüklenirken hata oluştu");
    } finally {
      setIsSearching(false);
    }
  }, []);

  const loadEventDocuments = useCallback(async (eventId: string) => {
    try {
      const response = await educationAPI.getEventDocuments(eventId);

      const items = response?.data?.items ?? [];

      const documentsData = items.map((d: any) => ({
        id: d.agreementDocumentId,
        agreementDocumentId: d.agreementDocumentId,
        agreementDocumentVersionId: d.agreementDocumentVersionId,
        name: d.name || d.title || "Doküman",
        size: d.size ?? 0,
        type:
          (d.name || d.title || "").split(".").pop()?.toLowerCase() ||
          "unknown",
        uploadedAt: d.createdAt,
        version: d.version,
        isFromDatabase: true,
      }));

      setDocuments(documentsData);
    } catch (error) {
      console.error("Error loading event documents:", error);
      toast.error("Dokümanlar yüklenirken hata oluştu");
    }
  }, []);

  const handleSyncReceivables = async () => {
    if (!editingEventId) return;

    setIsSyncing(true);
    try {
      const response = await financeAPI.syncEventReceivables(editingEventId);

      if (response.success) {
        toast.success("Ödeme İstekleri başarıyla oluşturuldu");
        setShowSyncConfirmation(false);
        // Refresh payment data
        await loadPaymentData();
      } else {
        toast.error(
          response.message || "Ödeme İsteği oluşturulurken hata oluştu",
        );
      }
    } catch (error) {
      console.error("Error syncing receivables:", error);
      toast.error("Ödeme İsteği oluşturulurken hata oluştu");
    } finally {
      setIsSyncing(false);
    }
  };

  // Load event details when component mounts or editingEventId changes
  useEffect(() => {
    if (isOpen && editingEventId) {
      loadEventDetails();
      loadClassrooms();
      loadAllStudents(); // Tüm öğrencileri yükle
    }
  }, [
    isOpen,
    editingEventId,
    loadEventDetails,
    loadClassrooms,
    loadAllStudents,
  ]);

  // Reset form when component closes
  useEffect(() => {
    if (!isOpen) {
      setFormData({
        id: "",
        name: "",
        description: "",
        startsAt: "",
        endsAt: "",
        lastPaymentDate: "",
        eventType: EventType.Trip,
        price: 0,
        currency: Currency.TRY,
        quotaType: 0,
        quota: 0,
      });
      setErrors({});
      setHasLimitedQuota(false);
      setQuota(undefined);
      // Öğrenci listesini de temizle
      setSearchResults([]);
      setSelectedSearchStudents([]);
      setSelectedAssignedStudents([]);
      setSearchTerm("");
      setSelectedClassrooms([]);
      // Dokuman durumlarını temizle
      setUploadStatus({});
      setDeleteStatus({});
      setSelectedDocumentsToDelete([]);
      setUploadedFiles([]);
    }
  }, [isOpen, schoolId]);

  // Helper function to convert local datetime to UTC ISO string
  const convertToUTC = (localDateTime: string): string => {
    if (!localDateTime) return "";
    const date = new Date(localDateTime);
    return date.toISOString();
  };

  // Student search and filter function
  const handleSearchStudents = async () => {
    // Eğer hiçbir filtre yoksa tüm öğrencileri yükle
    if (!searchTerm.trim() && selectedClassrooms.length === 0) {
      await loadAllStudents();
      return;
    }

    setIsSearching(true);
    try {
      // Seçilen sınıfların grade ve section bilgilerini al
      let gradeSectionFilters = undefined;
      if (selectedClassrooms.length > 0) {
        const selectedClassroomData = classrooms.filter((classroom) =>
          selectedClassrooms.includes(classroom.id),
        );

        gradeSectionFilters = selectedClassroomData.map((classroom) => ({
          grade: classroom.grade,
          section: classroom.section,
        }));
      }

      const apiParams = {
        Q: searchTerm.trim() || "",
        Page: 1,
        Size: 1000,
        OrderBy: 0,
        SortBy: "",
        // Grade ve section filtrelerini ekle
        GradeSectionFilters: gradeSectionFilters,
      };

      console.log("Filtreleme Parametreleri:", apiParams);

      const response = await educationAPI.getStudents(apiParams);

      if (response.data && response.data.items) {
        setSearchResults(response.data.items);
        const filterText = searchTerm.trim() ? `"${searchTerm}"` : "";
        const classText =
          selectedClassrooms.length > 0 ? `seçili sınıflardan` : "";
        toast.success(
          `${response.data.items.length} öğrenci bulundu ${filterText} ${classText}`,
        );
      }
    } catch (error) {
      console.error("Error filtering students:", error);
      toast.error("Öğrenci filtreleme sırasında hata oluştu");
    } finally {
      setIsSearching(false);
    }
  };

  const handleSelectSearchStudent = (studentId: string) => {
    if (!studentId) {
      return;
    }

    setSelectedSearchStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSelectAllSearchStudents = () => {
    if (selectedSearchStudents.length === searchResults.length) {
      setSelectedSearchStudents([]);
    } else {
      setSelectedSearchStudents(
        searchResults.map((s) => s.studentId || s.id).filter(Boolean),
      );
    }
  };

  const handleSelectAssignedStudent = (studentId: string) => {
    setSelectedAssignedStudents((prev) =>
      prev.includes(studentId)
        ? prev.filter((id) => id !== studentId)
        : [...prev, studentId],
    );
  };

  const handleSelectAllAssignedStudents = () => {
    if (selectedAssignedStudents.length === assignedStudents.length) {
      setSelectedAssignedStudents([]);
    } else {
      setSelectedAssignedStudents(
        assignedStudents.map((s) => s.studentId || s.id).filter(Boolean),
      );
    }
  };

  const handleBulkAddStudents = () => {
    const studentsToAdd = searchResults.filter((s) =>
      selectedSearchStudents.includes(s.studentId || s.id),
    );
    const newStudents = studentsToAdd.filter(
      (s) =>
        !assignedStudents.find(
          (as) => (as.studentId || as.id) === (s.studentId || s.id),
        ),
    );

    if (newStudents.length > 0) {
      // Add paymentStatus to new students (default to 'Unpaid' for new registrations)
      const studentsWithPaymentStatus = newStudents.map((student) => ({
        ...student,
        paymentStatus: "Unpaid",
      }));

      setAssignedStudents((prev) => [...prev, ...studentsWithPaymentStatus]);
      setSelectedSearchStudents([]);
      toast.success(`${newStudents.length} öğrenci etkinliğe eklendi`);
    } else {
      toast.error("Seçilen öğrenciler zaten etkinliğe kayıtlı");
    }
  };

  const handleBulkRemoveStudents = () => {
    setAssignedStudents(
      assignedStudents.filter(
        (s) => !selectedAssignedStudents.includes(s.studentId || s.id),
      ),
    );
    setSelectedAssignedStudents([]);
    toast.success(
      `${selectedAssignedStudents.length} öğrenci etkinlikten çıkarıldı`,
    );
  };

  // Document management functions
  const handleFileUpload = (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (files) {
      const newFiles = Array.from(files);

      // Filter out files that already exist in uploadedFiles (check by name and size)
      const uniqueFiles = newFiles.filter((newFile) => {
        // Check against uploadedFiles
        const existsInUploaded = uploadedFiles.some((existingFile) => {
          return (
            existingFile.name === newFile.name &&
            existingFile.size === newFile.size
          );
        });

        // Check against documents from database
        const existsInDocuments = documents.some((existingDoc) => {
          return (
            (existingDoc as any)?.name === newFile.name &&
            (existingDoc as any)?.size === newFile.size
          );
        });

        return !existsInUploaded && !existsInDocuments;
      });

      if (uniqueFiles.length === 0) {
        toast.error("Seçilen dosyalar zaten listede mevcut");
        event.target.value = "";
        return;
      }

      if (uniqueFiles.length < newFiles.length) {
        toast(
          `${newFiles.length - uniqueFiles.length} dosya zaten listede mevcut, ${uniqueFiles.length} yeni dosya eklendi`,
          {
            icon: "⚠️",
            duration: 4000,
          },
        );
      } else {
        toast.success(`${uniqueFiles.length} dosya seçildi`);
      }

      const processedFiles = uniqueFiles.map((file, index) => {
        const fileId = `temp-${file.name}-${file.size}-${index}`;
        setUploadStatus((prev) => ({ ...prev, [fileId]: "pending" }));
        return file;
      });

      setUploadedFiles((prev) => [...prev, ...processedFiles]);

      // Reset file input to allow selecting the same file again
      event.target.value = "";
    }
  };

  const handleDeleteDocument = (documentId: string | number) => {
    const docId = documentId.toString();
    if (docId.startsWith("temp-")) {
      // Client-side uploaded file - remove from list immediately
      setUploadedFiles((prev) =>
        prev.filter((file, index) => {
          const fileId = `temp-${file.name}-${file.size}-${index}`;
          return fileId !== docId;
        }),
      );
      setUploadStatus((prev) => {
        const newStatus = { ...prev };
        delete newStatus[docId];
        return newStatus;
      });
      toast.success("Dosya listeden kaldırıldı");
    } else {
      // Database document - add/remove from deletion list
      setSelectedDocumentsToDelete((prev) => {
        if (prev.includes(docId)) {
          toast.success("Dosya silme listesinden çıkarıldı");
          return prev.filter((id) => id !== docId);
        } else {
          toast.success("Dosya silme listesine eklendi");
          return [...prev, docId];
        }
      });
    }
  };

  // Download document using agreements API
  const handleDownloadDocument = async (doc: any) => {
    try {
      if (!doc.agreementDocumentId || !doc.version) {
        toast.error("Döküman bilgileri eksik");
        return;
      }

      toast.loading("Döküman indiriliyor...", { id: "download" });

      const { blob, contentType, fileName } =
        await agreementsAPI.getAgreementVersionBlob(
          doc.agreementDocumentId,
          doc.version,
        );

      console.log("Download debug:", {
        blobSize: blob.size,
        blobType: blob.type,
        contentType,
        fileName,
        docName: doc.name,
      });

      if (blob.size === 0) {
        toast.error("Döküman içeriği boş", { id: "download" });
        return;
      }

      // Determine filename with extension
      let downloadFileName = fileName || doc.name || "dokuman";
      // Add extension based on content type if not present
      if (!downloadFileName.includes(".")) {
        const extensionMap: Record<string, string> = {
          "application/pdf": ".pdf",
          "image/jpeg": ".jpg",
          "image/png": ".png",
          "image/gif": ".gif",
          "application/msword": ".doc",
          "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
            ".docx",
          "application/vnd.ms-excel": ".xls",
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
            ".xlsx",
        };
        const extension = extensionMap[contentType] || "";
        downloadFileName += extension;
      }

      // Create blob with correct type
      const downloadBlob = new Blob([blob], {
        type: contentType || blob.type || "application/octet-stream",
      });

      // Create download link and trigger download
      const url = window.URL.createObjectURL(downloadBlob);
      console.log(url, "__url");

      const link = document.createElement("a");
      link.href = url;
      link.download = downloadFileName;
      link.style.display = "none";

      document.body.appendChild(link);

      // Trigger download immediately (some browsers block delayed clicks)
      if (typeof link.download !== "undefined") {
        link.click();
      } else {
        // Fallback for older browsers
        window.open(url, "_blank", "noopener,noreferrer");
      }

      // Cleanup after a short delay
      setTimeout(() => {
        if (link.parentNode) {
          document.body.removeChild(link);
        }
        window.URL.revokeObjectURL(url);
      }, 1000);

      toast.success("Döküman başarıyla indirildi", { id: "download" });
    } catch (error) {
      console.error("Error downloading document:", error);
      toast.error("Döküman indirilirken hata oluştu", { id: "download" });
    }
  };

  const handleSelectDocumentForDeletion = (documentId: string) => {
    setSelectedDocumentsToDelete((prev) =>
      prev.includes(documentId)
        ? prev.filter((id) => id !== documentId)
        : [...prev, documentId],
    );
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return "0 Bytes";
    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  };

  const getFileIcon = (fileType: string) => {
    if (fileType.includes("pdf"))
      return <File className="w-4 h-4 text-red-500" />;
    if (fileType.includes("word") || fileType.includes("document"))
      return <File className="w-4 h-4 text-blue-500" />;
    if (fileType.includes("image"))
      return <File className="w-4 h-4 text-green-500" />;
    return <File className="w-4 h-4 text-gray-500" />;
  };

  const handleInputChange = (field: keyof UpdateEventRequest, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));

    // Clear error when user starts typing
    if (errors[field]) {
      setErrors((prev) => ({
        ...prev,
        [field]: "",
      }));
    }
  };

  const handlePaidEventChange = (checked: boolean) => {
    setIsPaidEvent(checked);
    if (!checked) {
      // Ücretli etkinlik seçilmediğinde fiyatı 0 yap
      setFormData((prev) => ({
        ...prev,
        price: 0,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    if (!formData.name.trim()) {
      newErrors.name = "Etkinlik adı zorunludur";
    }
    if (!formData.description.trim()) {
      newErrors.description = "Açıklama zorunludur";
    }
    if (!formData.startsAt) {
      newErrors.startsAt = "Başlangıç tarihi zorunludur";
    }
    if (!formData.endsAt) {
      newErrors.endsAt = "Bitiş tarihi zorunludur";
    }
    if (isPaidEvent && !formData.lastPaymentDate) {
      newErrors.lastPaymentDate = "Son ödeme tarihi zorunludur";
    }
    if (formData.price < 0) {
      newErrors.price = "Fiyat negatif olamaz";
    }

    // Validate date logic
    if (formData.startsAt && formData.endsAt) {
      const startDate = new Date(formData.startsAt);
      const endDate = new Date(formData.endsAt);
      if (startDate >= endDate) {
        newErrors.endsAt = "Bitiş tarihi başlangıç tarihinden sonra olmalıdır";
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);
    try {
      // Convert datetime fields to UTC before sending
      const eventData = {
        ...formData,
        startsAt: convertToUTC(formData.startsAt),
        endsAt: convertToUTC(formData.endsAt),
        lastPaymentDate: formData.lastPaymentDate
          ? convertToUTC(formData.lastPaymentDate)
          : null,
        price: isPaidEvent ? (formData.price ?? 0) : 0,
        currency:
          typeof formData.currency === "string"
            ? formData.currency === "TRY"
              ? 0
              : formData.currency === "USD"
                ? 1
                : formData.currency === "EUR"
                  ? 2
                  : formData.currency === "GBP"
                    ? 3
                    : 0
            : formData.currency,
        quotaType: hasLimitedQuota ? 1 : 0,
        quota: hasLimitedQuota ? (quota ?? 0) : 0,
      };

      await educationAPI.updateEvent(editingEventId!, eventData);
      toast.success("Etkinlik başarıyla güncellendi");
      setActiveTab("students");
    } catch (error: any) {
      console.error("Error updating event:", error);
      toast.error(
        error.response?.data?.message ||
          "Etkinlik güncellenirken bir hata oluştu",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveStudentsAndContinue = async () => {
    if (!editingEventId) {
      setActiveTab("documents");
      return;
    }

    setIsSubmitting(true);
    try {
      const studentIds = assignedStudents
        .map((student) => student.studentId || student.id)
        .filter(Boolean);

      await educationAPI.bulkAddStudentsToEvent(editingEventId, studentIds);

      if (assignedStudents.length === 0) {
        toast.success("Tüm katılımcılar etkinlikten çıkarıldı");
      } else {
        toast.success(
          `${assignedStudents.length} öğrenci etkinliğe kaydedildi`,
        );
      }

      // If event is activated, refresh payment data
      if (isActivated) {
        await loadPaymentData();
      }

      setActiveTab("documents");
    } catch (error: any) {
      console.error("Error saving students:", error);
      toast.error(
        error.response?.data?.message ||
          "Öğrenciler kaydedilirken bir hata oluştu",
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-7xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-[#9B51E0] rounded-lg flex items-center justify-center">
              <Calendar className="w-5 h-5 text-white" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-gray-900">
                Etkinlik Düzenle
              </h2>
              <p className="text-sm text-gray-500">
                Etkinlik bilgilerini güncelleyin
              </p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8 px-6">
            <button
              type="button"
              onClick={() => setActiveTab("event")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "event"
                  ? "border-[#9B51E0] text-[#9B51E0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Calendar className="w-4 h-4 inline mr-2" />
              Etkinlik Bilgileri
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("students")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "students"
                  ? "border-[#9B51E0] text-[#9B51E0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Users className="w-4 h-4 inline mr-2" />
              Öğrenci Ataması
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("documents")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "documents"
                  ? "border-[#9B51E0] text-[#9B51E0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <File className="w-4 h-4 inline mr-2" />
              Dokümanlar
            </button>
            <button
              type="button"
              onClick={() => setActiveTab("payments")}
              className={`py-4 px-1 border-b-2 font-medium text-sm ${
                activeTab === "payments"
                  ? "border-[#9B51E0] text-[#9B51E0]"
                  : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
              }`}
            >
              <Coins className="w-4 h-4 inline mr-2" />
              Ödeme İstekleri
            </button>
          </nav>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#9B51E0] mx-auto mb-2"></div>
                <p className="text-gray-500">
                  Etkinlik bilgileri yükleniyor...
                </p>
              </div>
            </div>
          ) : (
            <>
              {activeTab === "event" ? (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    {/* Left Column */}
                    <div className="space-y-6">
                      {/* Event Name */}
                      <div className="space-y-2">
                        <Label
                          htmlFor="name"
                          className="text-sm font-medium text-gray-700"
                        >
                          <Tag className="w-4 h-4 inline mr-2" />
                          Etkinlik Adı *
                        </Label>
                        <Input
                          id="name"
                          type="text"
                          value={formData.name}
                          onChange={(e) =>
                            handleInputChange("name", e.target.value)
                          }
                          placeholder="Etkinlik adını girin"
                          className={errors.name ? "border-red-500" : ""}
                        />
                        {errors.name && (
                          <p className="text-sm text-red-600">{errors.name}</p>
                        )}
                      </div>

                      {/* Event Type */}
                      <div className="space-y-2">
                        <Label
                          htmlFor="eventType"
                          className="text-sm font-medium text-gray-700"
                        >
                          <Tag className="w-4 h-4 inline mr-2" />
                          Etkinlik Türü *
                        </Label>
                        <SimpleDropdown
                          options={eventTypeOptions}
                          value={
                            eventTypeOptions.find(
                              (option) => option.value === formData.eventType,
                            ) || null
                          }
                          onChange={(option) =>
                            handleInputChange(
                              "eventType",
                              option?.value || EventType.Trip,
                            )
                          }
                          placeholder="Etkinlik türü seçin..."
                        />
                        {errors.eventType && (
                          <p className="text-sm text-red-600">
                            {errors.eventType}
                          </p>
                        )}
                      </div>

                      {/* Description */}
                      <div className="space-y-2">
                        <Label
                          htmlFor="description"
                          className="text-sm font-medium text-gray-700"
                        >
                          <FileText className="w-4 h-4 inline mr-2" />
                          Açıklama *
                        </Label>
                        <textarea
                          id="description"
                          value={formData.description}
                          onChange={(e) =>
                            handleInputChange("description", e.target.value)
                          }
                          placeholder="Etkinlik açıklamasını girin"
                          rows={6}
                          className={`w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent resize-none ${
                            errors.description ? "border-red-500" : ""
                          }`}
                        />
                        {errors.description && (
                          <p className="text-sm text-red-600">
                            {errors.description}
                          </p>
                        )}
                      </div>

                      {/* Dates */}
                      <div className="space-y-4">
                        <div className="space-y-2">
                          <Label
                            htmlFor="startsAt"
                            className="text-sm font-medium text-gray-700"
                          >
                            <Clock className="w-4 h-4 inline mr-2" />
                            Başlangıç Tarihi *
                          </Label>
                          <Input
                            id="startsAt"
                            type="datetime-local"
                            value={formData.startsAt}
                            onChange={(e) =>
                              handleInputChange("startsAt", e.target.value)
                            }
                            className={errors.startsAt ? "border-red-500" : ""}
                          />
                          {errors.startsAt && (
                            <p className="text-sm text-red-600">
                              {errors.startsAt}
                            </p>
                          )}
                        </div>

                        <div className="space-y-2">
                          <Label
                            htmlFor="endsAt"
                            className="text-sm font-medium text-gray-700"
                          >
                            <Clock className="w-4 h-4 inline mr-2" />
                            Bitiş Tarihi *
                          </Label>
                          <Input
                            id="endsAt"
                            type="datetime-local"
                            value={formData.endsAt}
                            onChange={(e) =>
                              handleInputChange("endsAt", e.target.value)
                            }
                            className={errors.endsAt ? "border-red-500" : ""}
                          />
                          {errors.endsAt && (
                            <p className="text-sm text-red-600">
                              {errors.endsAt}
                            </p>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Right Column */}
                    <div className="space-y-6">
                      {/* Paid Event Checkbox */}
                      <div className="space-y-2">
                        <div className="flex items-center space-x-2">
                          <input
                            type="checkbox"
                            id="isPaidEvent"
                            checked={isPaidEvent}
                            onChange={(e) =>
                              handlePaidEventChange(e.target.checked)
                            }
                            className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                          />
                          <Label
                            htmlFor="isPaidEvent"
                            className="text-sm font-medium text-gray-700 cursor-pointer"
                          >
                            <Coins className="w-4 h-4 inline mr-2" />
                            Ücretli Etkinlik
                          </Label>
                        </div>
                        <p className="text-xs text-gray-500 ml-6">
                          İşaretlenirse fiyat ve para birimi alanları aktif hale
                          gelir
                        </p>
                      </div>

                      {/* Kontenjan Switch */}
                      <div className="space-y-2">
                        <Label className="text-sm font-medium text-gray-700">
                          <Users className="w-4 h-4 inline mr-2" />
                          Kontenjan
                        </Label>
                        <div className="flex items-center space-x-4">
                          <button
                            type="button"
                            onClick={() => {
                              setHasLimitedQuota(false);
                              setQuota(undefined);
                            }}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              !hasLimitedQuota
                                ? "bg-[#9B51E0] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Sınırsız
                          </button>
                          <button
                            type="button"
                            onClick={() => setHasLimitedQuota(true)}
                            className={`px-4 py-2 rounded-md text-sm font-medium transition-colors ${
                              hasLimitedQuota
                                ? "bg-[#9B51E0] text-white"
                                : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                            }`}
                          >
                            Sınırlı
                          </button>
                        </div>
                        {hasLimitedQuota && (
                          <div className="mt-3">
                            <Label
                              htmlFor="quota"
                              className="text-sm font-medium text-gray-700"
                            >
                              Maksimum Katılımcı Sayısı
                            </Label>
                            <Input
                              id="quota"
                              type="number"
                              min="1"
                              value={quota || ""}
                              onChange={(e) =>
                                setQuota(
                                  e.target.value
                                    ? parseInt(e.target.value, 10)
                                    : undefined,
                                )
                              }
                              placeholder="Katılımcı sayısını girin"
                              className="mt-1"
                            />
                          </div>
                        )}
                      </div>

                      {/* Price and Currency */}
                      {isPaidEvent && (
                        <div className="space-y-4">
                          <div className="space-y-2">
                            <Label
                              htmlFor="price"
                              className="text-sm font-medium text-gray-700"
                            >
                              <Coins className="w-4 h-4 inline mr-2" />
                              Fiyat *
                            </Label>
                            <NumericFormat
                              value={formData.price || ""}
                              customInput={Input}
                              thousandSeparator="."
                              decimalSeparator=","
                              decimalScale={2}
                              fixedDecimalScale
                              allowNegative={false}
                              disabled={!isPaidEvent}
                              onValueChange={(values: any) => {
                                if (!isPaidEvent) return;

                                if (
                                  !values?.formattedValue ||
                                  values.formattedValue === ""
                                ) {
                                  handleInputChange("price", undefined);
                                } else {
                                  handleInputChange(
                                    "price",
                                    values?.floatValue ?? undefined,
                                  );
                                }
                              }}
                            />
                            {errors.price && (
                              <p className="text-sm text-red-600">
                                {errors.price}
                              </p>
                            )}
                          </div>

                          <div className="space-y-2">
                            <Label className="text-sm font-medium text-gray-700">
                              <Coins className="w-4 h-4 inline mr-2" />
                              Para Birimi *
                            </Label>
                            <SimpleDropdown
                              options={currencyOptions}
                              value={
                                currencyOptions.find(
                                  (opt) => opt.value === formData.currency,
                                ) || null
                              }
                              onChange={(option) =>
                                handleInputChange("currency", option?.value)
                              }
                              placeholder="Para birimi seçin"
                              disabled={!isPaidEvent}
                            />
                          </div>

                          <div className="space-y-2">
                            <Label
                              htmlFor="lastPaymentDate"
                              className="text-sm font-medium text-gray-700"
                            >
                              <Calendar className="w-4 h-4 inline mr-2" />
                              Son Ödeme Tarihi
                            </Label>
                            <Input
                              id="lastPaymentDate"
                              type="datetime-local"
                              value={formData.lastPaymentDate}
                              onChange={(e) =>
                                handleInputChange(
                                  "lastPaymentDate",
                                  e.target.value,
                                )
                              }
                              className={
                                errors.lastPaymentDate ? "border-red-500" : ""
                              }
                              disabled={!isPaidEvent}
                            />
                            {errors.lastPaymentDate && (
                              <p className="text-sm text-red-600">
                                {errors.lastPaymentDate}
                              </p>
                            )}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Tab 1 Action Buttons */}
                  <div className="flex items-center justify-end pt-6 border-t border-gray-200">
                    <Button
                      onClick={handleSubmit}
                      disabled={isSubmitting}
                      className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Kaydediliyor...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Kaydet ve İlerle
                        </>
                      )}
                    </Button>
                  </div>
                </form>
              ) : activeTab === "students" ? (
                <div className="space-y-6">
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 h-full">
                    {/* Sol Panel - Öğrenci Ara, Filtrele ve Listesi */}
                    <div className="space-y-6 h-full flex flex-col">
                      <Card className="flex-1 flex flex-col border-0 shadow-none">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center">
                              <Search className="w-5 h-5 mr-2 text-[#9B51E0]" />
                              Öğrenci Ara ve Filtrele
                            </CardTitle>
                            <Button
                              onClick={() => setShowStudentCreateForm(true)}
                              className="bg-green-600 hover:bg-green-700 text-white"
                              size="sm"
                            >
                              <Plus className="w-4 h-4 mr-2" />
                              Yeni Öğrenci Ekle
                            </Button>
                          </div>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col">
                          <div className="space-y-4 flex-1 flex flex-col">
                            {/* Arama Kutusu */}
                            <div className="space-y-2">
                              <div className="flex items-center space-x-2">
                                <div className="flex-1 relative">
                                  <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                                  <input
                                    type="text"
                                    placeholder="Ad, soyad veya numara ile ara..."
                                    value={searchTerm}
                                    onChange={(e) =>
                                      setSearchTerm(e.target.value)
                                    }
                                    onKeyDown={(e) => {
                                      if (e.key === "Enter") {
                                        handleSearchStudents();
                                      }
                                    }}
                                    className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-[#9B51E0] focus:border-transparent"
                                  />
                                  {searchTerm && (
                                    <button
                                      type="button"
                                      onClick={() => setSearchTerm("")}
                                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-600"
                                    >
                                      <X className="w-4 h-4" />
                                    </button>
                                  )}
                                </div>
                                <Button
                                  onClick={handleSearchStudents}
                                  disabled={isSearching}
                                  className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                                  size="sm"
                                >
                                  {isSearching
                                    ? "Filtreleniyor..."
                                    : "Filtrele"}
                                </Button>
                              </div>
                            </div>

                            {/* Sınıf ve Şube Filtreleri */}
                            <div className="space-y-2">
                              <MultiSelectDropdown
                                options={classrooms.map((classroom) => ({
                                  id: classroom.id,
                                  label: classroom.name,
                                  value: classroom.id,
                                  count: classroom.totalStudent || 0,
                                  grade: classroom.grade,
                                  section: classroom.section,
                                }))}
                                selectedValues={selectedClassrooms}
                                onChange={setSelectedClassrooms}
                                placeholder="Sınıf/Şube seçin (örn: 2A, 2B, 3. sınıf tamamı)"
                                disabled={loadingClassrooms}
                                loading={loadingClassrooms}
                                showClearButton={true}
                              />
                            </div>

                            {/* Öğrenci Listesi */}
                            <div className="space-y-2 flex-1 flex flex-col">
                              <div className="flex items-center justify-end">
                                {searchResults.length > 0 && (
                                  <Button
                                    onClick={handleBulkAddStudents}
                                    disabled={
                                      selectedSearchStudents.length === 0
                                    }
                                    className="bg-green-600 hover:bg-green-700 text-white"
                                    size="sm"
                                  >
                                    <Plus className="w-4 h-4 mr-2" />
                                    Seçili Kayıtları Ekle (
                                    {selectedSearchStudents.length})
                                  </Button>
                                )}
                              </div>
                              {searchResults.length === 0 ? (
                                <div className="text-center py-8 text-gray-500">
                                  <Search className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                                  <p className="text-sm">
                                    {searchTerm || selectedClassrooms.length > 0
                                      ? "Arama kriterlerine uygun öğrenci bulunamadı"
                                      : "Öğrenci aramak için yukarıdaki arama kutusunu kullanın"}
                                  </p>
                                </div>
                              ) : (
                                <div className="border border-gray-200 rounded-lg overflow-hidden h-[400px] flex flex-col">
                                  <div className="flex-1 overflow-y-auto">
                                    <table className="w-full">
                                      <thead className="bg-gray-50">
                                        <tr>
                                          <th className="w-12 px-2 py-3 text-center">
                                            <button
                                              onClick={
                                                handleSelectAllSearchStudents
                                              }
                                              className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                                              title="Tümünü Seç"
                                            >
                                              {selectedSearchStudents.length ===
                                              searchResults.length ? (
                                                <CheckSquare className="w-4 h-4" />
                                              ) : (
                                                <Square className="w-4 h-4" />
                                              )}
                                            </button>
                                          </th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                            Öğrenci
                                          </th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                            Numara
                                          </th>
                                          <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                            Sınıf
                                          </th>
                                        </tr>
                                      </thead>
                                      <tbody className="divide-y divide-gray-200">
                                        {searchResults.map((student) => (
                                          <tr
                                            key={
                                              student.studentId || student.id
                                            }
                                            className={`hover:bg-gray-50 ${selectedSearchStudents.includes(student.studentId || student.id) ? "bg-blue-50" : ""}`}
                                          >
                                            <td className="w-12 px-2 py-3 text-center">
                                              <button
                                                onClick={() =>
                                                  handleSelectSearchStudent(
                                                    student.studentId ||
                                                      student.id,
                                                  )
                                                }
                                                className="flex items-center justify-center"
                                              >
                                                {selectedSearchStudents.includes(
                                                  student.studentId ||
                                                    student.id,
                                                ) ? (
                                                  <CheckSquare className="w-4 h-4 text-blue-600" />
                                                ) : (
                                                  <Square className="w-4 h-4 text-gray-400" />
                                                )}
                                              </button>
                                            </td>
                                            <td className="px-4 py-3">
                                              <span className="font-medium text-gray-900">
                                                {student.firstName}{" "}
                                                {student.lastName}
                                              </span>
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                              {student.studentNumber}
                                            </td>
                                            <td className="px-4 py-3 text-sm text-gray-600">
                                              {(student as any).classroomName ||
                                                student.className ||
                                                "-"}
                                            </td>
                                          </tr>
                                        ))}
                                      </tbody>
                                    </table>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    </div>

                    {/* Sağ Panel - Etkinlik Katılımcıları */}
                    <div className="space-y-6 h-full flex flex-col">
                      <Card className="flex-1 flex flex-col border-0 shadow-none">
                        <CardHeader className="pb-4">
                          <div className="flex items-center justify-between">
                            <CardTitle className="text-lg flex items-center">
                              <Users className="w-5 h-5 mr-2 text-green-600" />
                              Etkinlik Katılımcıları
                            </CardTitle>
                            {assignedStudents.length > 0 && (
                              <Button
                                onClick={handleBulkRemoveStudents}
                                disabled={selectedAssignedStudents.length === 0}
                                variant="outline"
                                className="text-red-600 hover:text-red-700 hover:bg-red-50"
                                size="sm"
                              >
                                <Trash2 className="w-4 h-4 mr-2" />
                                Seçili Kayıtları Sil (
                                {selectedAssignedStudents.length})
                              </Button>
                            )}
                          </div>
                        </CardHeader>
                        <CardContent className="flex-1 flex flex-col">
                          {assignedStudents.length === 0 ? (
                            <div className="text-center py-8 text-gray-500">
                              <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                              <p>Henüz öğrenci atanmamış</p>
                            </div>
                          ) : (
                            <div className="flex-1 flex flex-col">
                              <div className="border border-gray-200 rounded-lg overflow-hidden flex-1 flex flex-col">
                                <div className="flex-1 overflow-y-auto">
                                  <table className="w-full">
                                    <thead className="bg-gray-50">
                                      <tr>
                                        <th className="w-12 px-2 py-3 text-center">
                                          <button
                                            onClick={
                                              handleSelectAllAssignedStudents
                                            }
                                            className="flex items-center justify-center text-sm font-medium text-gray-700 hover:text-gray-900"
                                            title="Tümünü Seç"
                                          >
                                            {selectedAssignedStudents.length ===
                                            assignedStudents.length ? (
                                              <CheckSquare className="w-4 h-4" />
                                            ) : (
                                              <Square className="w-4 h-4" />
                                            )}
                                          </button>
                                        </th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                          Öğrenci
                                        </th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                          Numara
                                        </th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                          Sınıf
                                        </th>
                                        <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">
                                          Durum
                                        </th>
                                      </tr>
                                    </thead>
                                    <tbody className="divide-y divide-gray-200">
                                      {assignedStudents.map((student) => (
                                        <tr
                                          key={student.studentId || student.id}
                                          className={`hover:bg-gray-50 ${selectedAssignedStudents.includes(student.studentId || student.id) ? "bg-red-50" : ""}`}
                                        >
                                          <td className="w-12 px-2 py-3 text-center">
                                            <button
                                              onClick={() =>
                                                handleSelectAssignedStudent(
                                                  student.studentId ||
                                                    student.id,
                                                )
                                              }
                                              className="flex items-center justify-center"
                                            >
                                              {selectedAssignedStudents.includes(
                                                student.studentId || student.id,
                                              ) ? (
                                                <CheckSquare className="w-4 h-4 text-red-600" />
                                              ) : (
                                                <Square className="w-4 h-4 text-gray-400" />
                                              )}
                                            </button>
                                          </td>
                                          <td className="px-4 py-3">
                                            <span className="font-medium text-gray-900">
                                              {student.firstName}{" "}
                                              {student.lastName}
                                            </span>
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            {student.studentNumber}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            {(student as any).classroomName ||
                                              student.className ||
                                              "-"}
                                          </td>
                                          <td className="px-4 py-3 text-sm text-gray-600">
                                            <span
                                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                                                (student as any)
                                                  .paymentStatus === "Unpaid"
                                                  ? "bg-yellow-100 text-yellow-800"
                                                  : "bg-green-100 text-green-800"
                                              }`}
                                            >
                                              {(student as any)
                                                .paymentStatus === "Unpaid"
                                                ? "Ödeme Bekliyor"
                                                : "Aktif"}
                                            </span>
                                          </td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              </div>
                            </div>
                          )}
                        </CardContent>
                      </Card>
                    </div>
                  </div>

                  {/* Tab 2 Action Buttons */}
                  <div className="flex items-center justify-end pt-6 border-t border-gray-200">
                    <Button
                      onClick={handleSaveStudentsAndContinue}
                      disabled={isSubmitting}
                      className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                    >
                      {isSubmitting ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Kaydediliyor...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Kaydet ve İlerle
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : activeTab === "documents" ? (
                <div className="space-y-6 flex flex-col h-full">
                  {/* Document Upload Section */}
                  <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center hover:border-gray-400 transition-colors">
                    <Upload className="w-8 h-8 mx-auto mb-2 text-gray-400" />
                    <p className="text-gray-600 mb-3 text-sm">
                      Dosyaları buraya sürükleyin veya seçin
                    </p>
                    <input
                      type="file"
                      multiple
                      onChange={handleFileUpload}
                      className="hidden"
                      id="file-upload"
                    />
                    <label
                      htmlFor="file-upload"
                      className="bg-[#9B51E0] text-white px-3 py-1.5 rounded-md hover:bg-[#8B41D0] cursor-pointer inline-block text-sm"
                    >
                      Dosya Seç
                    </label>
                  </div>

                  {/* Document List Section */}
                  <Card className="flex-1">
                    <CardHeader>
                      <CardTitle className="text-lg">
                        Mevcut Dokümanlar
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="flex-1">
                      {documents.length === 0 && uploadedFiles.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <File className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg">Henüz doküman yüklenmemiş</p>
                          <p className="text-sm mt-2">
                            Yukarıdaki alanı kullanarak doküman yükleyin
                          </p>
                        </div>
                      ) : (
                        <div className="max-h-[500px] overflow-y-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50 sticky top-0">
                              <tr>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  <input
                                    type="checkbox"
                                    checked={
                                      selectedDocumentsToDelete.length ===
                                        documents.filter(
                                          (doc) =>
                                            deleteStatus[doc.id.toString()] !==
                                            "success",
                                        ).length &&
                                      documents.filter(
                                        (doc) =>
                                          deleteStatus[doc.id.toString()] !==
                                          "success",
                                      ).length > 0
                                    }
                                    onChange={(e) => {
                                      const availableDocuments =
                                        documents.filter(
                                          (doc) =>
                                            deleteStatus[doc.id.toString()] !==
                                            "success",
                                        );
                                      if (e.target.checked) {
                                        setSelectedDocumentsToDelete(
                                          availableDocuments.map((doc) =>
                                            doc.id.toString(),
                                          ),
                                        );
                                      } else {
                                        setSelectedDocumentsToDelete([]);
                                      }
                                    }}
                                    className="w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2"
                                  />
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Dosya
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Boyut
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Tarih
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Durum
                                </th>
                                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  İşlemler
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {/* Database Documents */}
                              {documents.map((doc) => {
                                const isDeleted =
                                  deleteStatus[doc.id.toString()] === "success";
                                return (
                                  <tr
                                    key={doc.id}
                                    className={`hover:bg-gray-50 ${isDeleted ? "opacity-60" : ""}`}
                                  >
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <input
                                        type="checkbox"
                                        checked={selectedDocumentsToDelete.includes(
                                          doc.id.toString(),
                                        )}
                                        onChange={() =>
                                          handleSelectDocumentForDeletion(
                                            doc.id.toString(),
                                          )
                                        }
                                        disabled={isDeleted}
                                        className={`w-4 h-4 text-[#9B51E0] bg-gray-100 border-gray-300 rounded focus:ring-[#9B51E0] focus:ring-2 ${isDeleted ? "opacity-50 cursor-not-allowed" : ""}`}
                                      />
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        {getFileIcon(doc.type)}
                                        <div className="ml-3">
                                          <div
                                            className={`text-sm font-medium ${isDeleted ? "text-gray-500" : "text-gray-900"}`}
                                          >
                                            {doc.name}
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            Veritabanından
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td
                                      className={`px-4 py-4 whitespace-nowrap text-sm ${isDeleted ? "text-gray-400" : "text-gray-500"}`}
                                    >
                                      {formatFileSize(doc.size)}
                                    </td>
                                    <td
                                      className={`px-4 py-4 whitespace-nowrap text-sm ${isDeleted ? "text-gray-400" : "text-gray-500"}`}
                                    >
                                      {new Date(doc.uploadedAt).toLocaleString(
                                        "tr-TR",
                                      )}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <div className="flex flex-col space-y-1">
                                        <span
                                          className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-32 ${
                                            deleteStatus[doc.id.toString()] ===
                                            "deleting"
                                              ? "bg-blue-100 text-blue-800"
                                              : deleteStatus[
                                                    doc.id.toString()
                                                  ] === "success"
                                                ? "bg-green-100 text-green-800"
                                                : deleteStatus[
                                                      doc.id.toString()
                                                    ] === "error"
                                                  ? "bg-red-100 text-red-800"
                                                  : uploadStatus[
                                                        doc.id.toString()
                                                      ] === "success"
                                                    ? "bg-green-100 text-green-800"
                                                    : "bg-green-100 text-green-800"
                                          }`}
                                        >
                                          {deleteStatus[doc.id.toString()] ===
                                          "deleting"
                                            ? "Siliniyor"
                                            : deleteStatus[
                                                  doc.id.toString()
                                                ] === "success"
                                              ? "Silindi"
                                              : deleteStatus[
                                                    doc.id.toString()
                                                  ] === "error"
                                                ? "Silme Başarısız"
                                                : uploadStatus[
                                                      doc.id.toString()
                                                    ] === "success"
                                                  ? "Yüklendi"
                                                  : "Kayıtlı"}
                                        </span>
                                        {(deleteStatus[doc.id.toString()] ===
                                          "deleting" ||
                                          uploadStatus[doc.id.toString()] ===
                                            "uploading") && (
                                          <div className="w-32">
                                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                              <div className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-pulse"></div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                      <div className="flex items-center space-x-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            handleDownloadDocument(doc)
                                          }
                                          disabled={isDeleted || !doc.version}
                                          className={`text-blue-600 hover:text-blue-700 ${isDeleted || !doc.version ? "opacity-50 cursor-not-allowed" : ""}`}
                                          title={
                                            isDeleted
                                              ? "Silinmiş dosya indirilemez"
                                              : !doc.version
                                                ? "Versiyon bilgisi eksik"
                                                : "Dosyayı indir"
                                          }
                                        >
                                          <Download className="w-4 h-4" />
                                        </Button>
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            handleSelectDocumentForDeletion(
                                              doc.id.toString(),
                                            )
                                          }
                                          disabled={isDeleted}
                                          className={`${
                                            isDeleted
                                              ? "opacity-50 cursor-not-allowed text-gray-400"
                                              : selectedDocumentsToDelete.includes(
                                                    doc.id.toString(),
                                                  )
                                                ? "text-red-600 bg-red-50 border-red-300"
                                                : "text-red-600 hover:text-red-700"
                                          }`}
                                          title={
                                            isDeleted
                                              ? "Dosya zaten silinmiş"
                                              : selectedDocumentsToDelete.includes(
                                                    doc.id.toString(),
                                                  )
                                                ? "Silme listesinden çıkar"
                                                : "Silme listesine ekle"
                                          }
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}

                              {/* Client-side Uploaded Files */}
                              {uploadedFiles.map((file, index) => {
                                const fileId = `temp-${file.name}-${file.size}-${index}`;
                                const status =
                                  uploadStatus[fileId] || "pending";

                                return (
                                  <tr key={fileId} className="hover:bg-gray-50">
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <input
                                        type="checkbox"
                                        disabled
                                        className="w-4 h-4 text-gray-300 bg-gray-100 border-gray-300 rounded"
                                      />
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <div className="flex items-center">
                                        {getFileIcon(file.type)}
                                        <div className="ml-3">
                                          <div className="text-sm font-medium text-gray-900">
                                            {file.name}
                                          </div>
                                          <div className="text-sm text-gray-500">
                                            Yeni yüklenecek
                                          </div>
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {formatFileSize(file.size)}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                                      {new Date().toLocaleString("tr-TR")}
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap">
                                      <div className="flex flex-col space-y-1">
                                        <span
                                          className={`inline-flex items-center justify-center px-2.5 py-0.5 rounded-full text-xs font-medium w-32 ${
                                            status === "pending"
                                              ? "bg-yellow-100 text-yellow-800"
                                              : status === "uploading"
                                                ? "bg-blue-100 text-blue-800"
                                                : status === "success"
                                                  ? "bg-green-100 text-green-800"
                                                  : "bg-red-100 text-red-800"
                                          }`}
                                        >
                                          {status === "pending"
                                            ? "Bekliyor"
                                            : status === "uploading"
                                              ? "Yükleniyor"
                                              : status === "success"
                                                ? "Yüklendi"
                                                : "Yükleme başarısız"}
                                        </span>
                                        {status === "uploading" && (
                                          <div className="w-32">
                                            <div className="w-full bg-gray-200 rounded-full h-1.5 overflow-hidden">
                                              <div className="h-full bg-gradient-to-r from-blue-400 via-blue-500 to-blue-600 rounded-full animate-pulse"></div>
                                            </div>
                                          </div>
                                        )}
                                      </div>
                                    </td>
                                    <td className="px-4 py-4 whitespace-nowrap text-sm font-medium">
                                      <div className="flex items-center space-x-2">
                                        <Button
                                          size="sm"
                                          variant="outline"
                                          onClick={() =>
                                            handleDeleteDocument(fileId)
                                          }
                                          className="text-red-600 hover:text-red-700"
                                        >
                                          <Trash2 className="w-4 h-4" />
                                        </Button>
                                      </div>
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  {/* Tab 3 Action Buttons */}
                  <div className="flex items-center justify-between pt-6 border-t border-gray-200">
                    <div className="text-sm text-gray-600">
                      {selectedDocumentsToDelete.length > 0 && (
                        <span className="text-red-600">
                          {selectedDocumentsToDelete.length} dosya silinecek
                        </span>
                      )}
                      {uploadedFiles.length > 0 && (
                        <span
                          className={`ml-2 ${selectedDocumentsToDelete.length > 0 ? "ml-4" : ""}`}
                        >
                          {uploadedFiles.length} dosya yüklenecek
                        </span>
                      )}
                    </div>
                    <Button
                      onClick={async () => {
                        try {
                          // Set global uploading state
                          setIsUploadingAny(true);

                          // 1. First, delete selected documents from database (sırayla)
                          if (selectedDocumentsToDelete.length > 0) {
                            const successfullyDeletedDocuments: string[] = [];

                            for (const documentId of selectedDocumentsToDelete) {
                              const documentName =
                                documents.find(
                                  (doc) => doc.id.toString() === documentId,
                                )?.name || documentId;

                              // Set deleting status
                              setDeleteStatus((prev) => ({
                                ...prev,
                                [documentId]: "deleting",
                              }));

                              try {
                                await educationAPI.deleteDocument(
                                  editingEventId!,
                                  documentId,
                                );

                                // Set success status
                                setDeleteStatus((prev) => ({
                                  ...prev,
                                  [documentId]: "success",
                                }));
                                toast.success(
                                  `Dokuman silindi: ${documentName}`,
                                );
                                successfullyDeletedDocuments.push(documentId);
                              } catch (error) {
                                console.error("Delete error:", error);

                                // Set error status
                                setDeleteStatus((prev) => ({
                                  ...prev,
                                  [documentId]: "error",
                                }));
                                toast.error(
                                  `Dokuman silinirken hata: ${documentName}`,
                                );
                              }
                            }

                            // Keep successfully deleted documents in the list but mark them as deleted
                            // Clear selections for successfully deleted documents
                            setSelectedDocumentsToDelete((prev) =>
                              prev.filter(
                                (id) =>
                                  !successfullyDeletedDocuments.includes(id),
                              ),
                            );
                          }

                          // 2. Then, upload new files one by one (sırayla)
                          const successfullyUploadedFiles: any[] = [];
                          const failedUploadFiles: File[] = [];

                          for (let i = 0; i < uploadedFiles.length; i++) {
                            const file = uploadedFiles[i];
                            const fileId = `temp-${file.name}-${file.size}-${i}`;

                            setUploadStatus((prev) => ({
                              ...prev,
                              [fileId]: "uploading",
                            }));

                            try {
                              // Upload file using axiosClient
                              const response =
                                await educationAPI.uploadDocument(
                                  editingEventId!,
                                  file,
                                );

                              setUploadStatus((prev) => ({
                                ...prev,
                                [fileId]: "success",
                              }));
                              toast.success(`Dosya yüklendi: ${file.name}`);

                              // Add successfully uploaded file to documents list
                              if (response.data) {
                                const newDocumentId =
                                  response.data.agreementDocumentId ||
                                  response.data.id ||
                                  Date.now() + i;
                                successfullyUploadedFiles.push({
                                  id: newDocumentId,
                                  eventAgreementDocumentId:
                                    response.data.eventAgreementDocumentId,
                                  agreementDocumentVersionId:
                                    response.data.agreementDocumentVersionId,
                                  agreementDocumentId:
                                    response.data.agreementDocumentId,
                                  name: file.name,
                                  size: file.size,
                                  type: file.type,
                                  uploadedAt: new Date().toISOString(),
                                  version: response.data.version,
                                });

                                // Set upload status for the new document
                                setUploadStatus((prev) => ({
                                  ...prev,
                                  [newDocumentId]: "success",
                                }));
                              }
                            } catch (error) {
                              console.error("Upload error:", error);
                              setUploadStatus((prev) => ({
                                ...prev,
                                [fileId]: "error",
                              }));
                              toast.error(
                                `Dosya yüklenirken hata: ${file.name}`,
                              );

                              // Keep failed files in the list
                              failedUploadFiles.push(file);
                            }
                          }

                          // Add successfully uploaded files to documents list
                          if (successfullyUploadedFiles.length > 0) {
                            setDocuments((prev) => [
                              ...prev,
                              ...successfullyUploadedFiles,
                            ]);
                          }

                          // Clear selections and only remove successfully uploaded files
                          setUploadedFiles(failedUploadFiles); // Keep only failed files

                          // Clear status for successfully uploaded files
                          const newUploadStatus: {
                            [key: string]:
                              | "pending"
                              | "uploading"
                              | "success"
                              | "error";
                          } = {};

                          // Keep status for failed files
                          failedUploadFiles.forEach((file, index) => {
                            const fileId = `temp-${file.name}-${file.size}-${index}`;
                            newUploadStatus[fileId] = "error";
                          });

                          setUploadStatus(newUploadStatus);

                          // Reset global uploading state
                          setIsUploadingAny(false);

                          // Show appropriate success message
                          if (
                            successfullyUploadedFiles.length > 0 &&
                            failedUploadFiles.length === 0
                          ) {
                            toast.success(
                              `${successfullyUploadedFiles.length} dosya başarıyla yüklendi`,
                            );
                          } else if (
                            successfullyUploadedFiles.length > 0 &&
                            failedUploadFiles.length > 0
                          ) {
                            toast(
                              `${successfullyUploadedFiles.length} dosya yüklendi, ${failedUploadFiles.length} dosya başarısız`,
                              {
                                icon: "⚠️",
                                duration: 5000,
                              },
                            );
                          } else if (selectedDocumentsToDelete.length > 0) {
                            toast.success(
                              `${selectedDocumentsToDelete.length} dokuman silindi`,
                            );
                          } else {
                            toast.error("Hiçbir işlem yapılamadı");
                          }
                        } catch (error) {
                          console.error("Error saving documents:", error);
                          toast.error(
                            "Dokümanlar kaydedilirken bir hata oluştu",
                          );
                          setIsUploadingAny(false);
                        }
                      }}
                      disabled={
                        (selectedDocumentsToDelete.length === 0 &&
                          uploadedFiles.length === 0) ||
                        isUploadingAny
                      }
                      className="bg-[#9B51E0] hover:bg-[#8B41D0] disabled:bg-gray-300 disabled:cursor-not-allowed"
                    >
                      {isUploadingAny ? (
                        <>
                          <div className="w-4 h-4 mr-2 animate-spin rounded-full border-2 border-white border-t-transparent"></div>
                          Yükleniyor...
                        </>
                      ) : (
                        <>
                          <Save className="w-4 h-4 mr-2" />
                          Kaydet
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              ) : activeTab === "payments" ? (
                <div className="space-y-6">
                  {/* Payment Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Toplam Ödeme İsteği
                          </p>
                          <p className="text-2xl font-bold text-gray-900">
                            {receivables.length}
                          </p>
                        </div>
                        <Users className="w-8 h-8 text-blue-500" />
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Ödenen
                          </p>
                          <p className="text-2xl font-bold text-green-600">
                            {
                              receivables.filter((r) => r.status === "Paid")
                                .length
                            }
                          </p>
                        </div>
                        <Coins className="w-8 h-8 text-green-500" />
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            Bekleyen
                          </p>
                          <p className="text-2xl font-bold text-yellow-600">
                            {
                              receivables.filter((r) => r.status === "Pending")
                                .length
                            }
                          </p>
                        </div>
                        <Calendar className="w-8 h-8 text-yellow-500" />
                      </div>
                    </Card>

                    <Card className="p-4">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-sm font-medium text-gray-600">
                            İptal Edilenler
                          </p>
                          <p className="text-2xl font-bold text-red-600">
                            {
                              receivables.filter(
                                (r) => r.status === "Cancelled",
                              ).length
                            }
                          </p>
                        </div>
                        <X className="w-8 h-8 text-red-500" />
                      </div>
                    </Card>
                  </div>

                  {/* Payment Table */}
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg flex items-center">
                        <Coins className="w-5 h-5 mr-2 text-[#9B51E0]" />
                        Ödeme İsteği Durumları
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      {receivables.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                          <Coins className="w-16 h-16 mx-auto mb-4 text-gray-300" />
                          <p className="text-lg">
                            Henüz ödeme isteği oluşturulmamış
                          </p>
                          <p className="text-sm mt-2">
                            Ödeme istekleri bu etkinlik aktif edilince otomatik
                            olarak oluşturulacak
                          </p>
                          <Button
                            onClick={() => setShowSyncConfirmation(true)}
                            className="mt-4 bg-[#9B51E0] hover:bg-[#8B41D0] text-white"
                          >
                            <Coins className="w-4 h-4 mr-2" />
                            Etkinliği Aktif Et ve Ödeme İsteklerini Oluştur
                          </Button>
                        </div>
                      ) : (
                        <div className="overflow-x-auto">
                          <table className="w-full">
                            <thead className="bg-gray-50">
                              <tr>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Öğrenci
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Tutar
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Vade Tarihi
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Durum
                                </th>
                                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                  Oluşturulma Tarihi
                                </th>
                              </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                              {receivables.map((receivable) => {
                                return (
                                  <tr
                                    key={receivable.receivableId}
                                    className="hover:bg-gray-50"
                                  >
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <div>
                                        <div className="text-sm font-medium text-gray-900">
                                          {receivable.studentName}
                                        </div>
                                      </div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                                      {receivable.amount.toLocaleString(
                                        "tr-TR",
                                      )}{" "}
                                      {receivable.currency}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {new Date(
                                        receivable.dueDate,
                                      ).toLocaleDateString("tr-TR")}
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap">
                                      <span
                                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                                          receivable.status === "Paid"
                                            ? "bg-green-100 text-green-800"
                                            : receivable.status ===
                                                "PartiallyPaid"
                                              ? "bg-blue-100 text-blue-800"
                                              : receivable.status ===
                                                  "Cancelled"
                                                ? "bg-gray-100 text-gray-800"
                                                : "bg-yellow-100 text-yellow-800"
                                        }`}
                                      >
                                        {receivable.status === "Paid"
                                          ? "Ödendi"
                                          : receivable.status ===
                                              "PartiallyPaid"
                                            ? "Kısmen Ödendi"
                                            : receivable.status === "Cancelled"
                                              ? "İptal Edildi"
                                              : "Beklemede"}
                                      </span>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                      {new Date(
                                        receivable.createdAt,
                                      ).toLocaleDateString("tr-TR")}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              ) : null}
            </>
          )}
        </div>

        {/* Sync Confirmation Modal */}
        {showSyncConfirmation && (
          <div className="fixed top-0 left-0 right-0 bottom-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 mt-0">
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md">
              <div className="p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-yellow-100 rounded-full flex items-center justify-center mr-3">
                    <Coins className="w-5 h-5 text-yellow-600" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900">
                    Ödeme İsteği Oluşturma Onayı
                  </h3>
                </div>
                <p className="text-gray-600 mb-6">
                  Bu etkinlik için ödeme istekleri oluşturulacak. Bu işlem geri
                  alınamaz. Devam etmek istediğinizden emin misiniz?
                </p>
                <div className="flex justify-end space-x-3">
                  <Button
                    variant="outline"
                    onClick={() => setShowSyncConfirmation(false)}
                    disabled={isSyncing}
                  >
                    İptal
                  </Button>
                  <Button
                    onClick={handleSyncReceivables}
                    disabled={isSyncing}
                    className="bg-[#9B51E0] hover:bg-[#8B41D0]"
                  >
                    {isSyncing ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Oluşturuluyor...
                      </>
                    ) : (
                      "Evet, Oluştur"
                    )}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Student Create Form Modal */}
      {showStudentCreateForm && (
        <StudentCreateForm
          isOpen={showStudentCreateForm}
          onClose={() => setShowStudentCreateForm(false)}
          onSuccess={() => {
            setShowStudentCreateForm(false);
            // Refresh student search results
            handleSearchStudents();
            toast.success("Öğrenci başarıyla eklendi ve liste güncellendi");
          }}
          schoolId={schoolId}
        />
      )}
    </div>
  );
};

export default EventEditForm;
